import { BUILD_COST } from '@/billing/buildCostNote';
import { trackPixel } from '../analytics/metaPixel';

/**
 * The trial, as the builder offers it — the owner's decision of 2026-09-25, confirmed
 * 2026-09-29: the free tier is gone for everyone, and an app is built on a 7-day free trial with
 * the card taken up front, then $20/month. The card is asked for at the Plan screen's Build
 * button, right before the build, and every subscription checkout the server makes is that
 * trial, whoever asks for it.
 *
 * The words live here once, so the Plan screen, the Credits screen and the return from Stripe
 * cannot drift apart on what somebody is being asked to agree to.
 */
// 14 days since 2026-10-12 (owner's decision; 3 since 2026-10-01, 7 before). The server's TRIAL_DAYS (infra/lambda/shared/paywall.ts)
// sets what Stripe actually gives, and these words must say the same number.
export const TRIAL_DAYS = 14;
export const TRIAL_HEADLINE = `${TRIAL_DAYS} days free, then $20/month.`;
export const TRIAL_DETAIL = `Card required; nothing is charged for ${TRIAL_DAYS} days. Cancel any time from Account.`;
/** Said, whole, before anybody is sent to Stripe. */
export const TRIAL_TERMS = `${TRIAL_HEADLINE} ${TRIAL_DETAIL}`;

/**
 * What a checkout sells this account: the trial, or the plan from day one.
 *
 * One trial per account (the server, 2026-09-29): `createCheckout` sells an account that has had
 * its week the plan with nothing free about it, and `/credits` says `trialAvailable` so a paywall
 * does not promise what the checkout will not sell — "7 days free" over a $20 charge on the spot.
 * Absent reads as the trial: a server from before the field sold it to everybody, and that is what
 * the account will be sold.
 */
export type Offer = 'trial' | 'subscribe';

export function offerFor(balance: { trialAvailable?: boolean }): Offer {
  return balance.trialAvailable === false ? 'subscribe' : 'trial';
}

/** The plan from day one, said as what it is. Nothing about a free week this account has had. */
export const SUBSCRIBE_BUTTON = 'Subscribe — $20/month';
const SUBSCRIBE_HEADLINE = '$20/month, starting today.';
const SUBSCRIBE_DETAIL = 'This account has already had its trial week, so your card is charged $20 now and then each month. Cancel any time from Account.';

/** What is said before the button that leads to `offer`'s checkout. */
export function offerTerms(offer: Offer): { headline: string; detail: string } {
  return offer === 'subscribe'
    ? { headline: SUBSCRIBE_HEADLINE, detail: SUBSCRIBE_DETAIL }
    : { headline: TRIAL_HEADLINE, detail: TRIAL_DETAIL };
}

/** The button that leads to it, named for what it does. */
export function offerButton(offer: Offer): string {
  return offer === 'subscribe' ? SUBSCRIBE_BUTTON : `Start ${TRIAL_DAYS}-day free trial`;
}

/*
 * V6's free week (2026-10-14): an account's first app goes live free for 7 days with no card, and
 * the offer is to keep it live after that. Subscribing during the week takes the card now and makes
 * the first $20 charge on the free-until date (the server, 2026-10-14), so the words say exactly
 * that. Once the week has ended the app is paused and the plan starts at once.
 */
export const KEEP_LIVE_BUTTON = 'Keep it live: $20/month';

/** The free week's end as people say a date: "October 21" (or "Oct 21", `short`, where room is tight). */
export function freeUntilDate(iso: string, short = false): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', { month: short ? 'short' : 'long', day: 'numeric' });
}

/** The offer to a free-week account, in its words: while the week runs, and once it has ended. */
export function keepLiveTerms(
  appName: string,
  untilIso: string,
  now = Date.now(),
): { title: string; headline: string; detail: string; button: string } {
  const until = Date.parse(untilIso);
  if (Number.isFinite(until) && until <= now) {
    return {
      title: `${appName} is paused`,
      headline: 'Keep it live: $20/month.',
      detail: 'Cancel any time.',
      button: KEEP_LIVE_BUTTON,
    };
  }
  const date = freeUntilDate(untilIso);
  return {
    title: `Keep ${appName} live`,
    headline: date ? `Nothing is charged until ${date}.` : 'Nothing is charged this week.',
    detail: 'Then $20/month. Cancel any time from Account.',
    button: 'Keep it live',
  };
}

/** Whether a free week is running now for this balance: recorded, not over, and not already subscribed. */
export function freeWeekRunning(balance: { freeWeek?: { until: string }; subscribed?: boolean; tier?: string }, now = Date.now()): boolean {
  if (!balance.freeWeek || balance.subscribed === true || balance.tier === 'paid' || balance.tier === 'unlimited') return false;
  const until = Date.parse(balance.freeWeek.until);
  return Number.isFinite(until) && until > now;
}

/*
 * Which offer the checkout this tab last left for was. Stripe's return address is the server's to
 * write (success_url), so it cannot say; the return page reads it here to greet a subscription as
 * one, and not report StartTrial for a trial that never began. In sessionStorage because the trip
 * to Stripe is this tab's, and for a day, like the plan put away for it (draftStash.ts).
 *
 * No record — storage blocked, or a checkout begun in another tab — reads as the trial, which is
 * how every return was read before one trial per account; the checkout itself is the server's.
 */
const OFFER_KEY = 'saddle.checkoutOffer';
const OFFER_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Called right before the browser leaves for a subscription's checkout. */
export function rememberCheckoutOffer(offer: Offer): void {
  try {
    sessionStorage.setItem(OFFER_KEY, JSON.stringify({ offer, at: Date.now() }));
  } catch {
    /* read as the trial on the way back: see above */
  }
}

/** The offer the last checkout from this tab was, or null when there is no record of one. */
export function checkoutOffered(): Offer | null {
  try {
    const record = JSON.parse(sessionStorage.getItem(OFFER_KEY) ?? 'null') as { offer?: unknown; at?: unknown } | null;
    if (!record || (record.offer !== 'trial' && record.offer !== 'subscribe') || typeof record.at !== 'number') return null;
    if (!(Date.now() - record.at <= OFFER_MAX_AGE_MS)) return null;
    return record.offer;
  } catch {
    return null;
  }
}

/**
 * Whether this account builds without meeting the checkout.
 *
 * The paid or unlimited TIER, a trial included — the webhook grants the paid tier the moment a
 * trial starts (`checkout.session.completed` with `no_payment_required`). A promo code's paid tier
 * builds too: those are credits the owner gave.
 *
 * And any account that holds a build's worth of credits, whatever its tier. The owner's decision
 * is that existing accounts keep their credits and spend them at the normal prices, and the server
 * builds for them (webBuild.ts on lane/trial-server: "it keeps its credits and spends them at the
 * normal prices"). Judged on the tier alone, a free account holding 5,000 credits was sent to the
 * trial checkout and could not spend them. The brief's "the tier, whatever credits it still holds"
 * said otherwise; the owner's decision wins, and review found the conflict (2026-09-29).
 *
 * Anything else, a tier this client has never heard of included, is offered the trial — the
 * server charges and refuses on its own account, so the safe mistake here is to offer.
 */
export function buildsIncluded(balance: { tier?: string; creditsRemaining?: number }): boolean {
  if (balance.tier === 'paid' || balance.tier === 'unlimited') return true;
  return typeof balance.creditsRemaining === 'number' && balance.creditsRemaining >= BUILD_COST;
}

/*
 * A digest, so the idea — somebody's own words — is not written into storage to key a counter.
 * FNV-1a: collisions would cost one missed InitiateCheckout, never anything a person sees.
 */
function digest(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** What this page has sent while storage refused to remember it. */
const sentHere = new Set<string>();

/**
 * Send `event` once for `key`, remembered in localStorage because the moments it guards straddle
 * a full page load — the trip to Stripe and back. Where storage cannot both read and write the
 * key, this page's memory keeps it instead, so it still sends once per page: a rare duplicate is
 * better than never knowing.
 *
 * Remembered only when `send` says the event went. Recorded before it, a press made while the
 * consent banner was unanswered — the pixel sends nothing then — used the key up, and the press
 * after a yes sent nothing (review, 2026-09-29).
 */
function once(key: string, send: () => boolean): boolean {
  if (sentHere.has(key)) return false;
  let readable = true;
  try {
    if (localStorage.getItem(key)) return false;
  } catch {
    readable = false;
  }
  if (!send()) return false;
  let kept = false;
  try {
    localStorage.setItem(key, '1');
    kept = readable;
  } catch {
    /* unwritable: kept below */
  }
  if (!kept) sentHere.add(key);
  return true;
}

/**
 * InitiateCheckout — the event the ads optimize for — once per DRAFT, not per press: Build,
 * cancel on Stripe, Build again is one checkout begun. A draft is this account's idea; a new
 * idea is a new one. Sent by the Plan screen right before it hands the browser to Stripe, which
 * is told whether anything went, so it can give the request a moment before the page goes.
 */
export function reportInitiateCheckout(ownerId: string, idea: string): boolean {
  return once(`saddle_initiate_checkout_sent:${digest(`${ownerId}\n${idea.trim()}`)}`, () => trackPixel('InitiateCheckout', { trialDays: TRIAL_DAYS }));
}

/** A checkout session id Stripe filled in — not absent (typed by hand), not its unfilled template. */
function realSessionId(sessionId: string | null): sessionId is string {
  return Boolean(sessionId) && !sessionId!.includes('{');
}

/**
 * StartTrial, once per checkout session, on the return from Stripe. Worth $0 today — the trial
 * charges nothing — and no predicted value is guessed at until there is retention to base it on.
 * Without a session id (typed by hand) or with Stripe's template unfilled, nothing is sent; nor
 * for a checkout that sold an account the plan from day one (`checkoutOffered`).
 */
export function reportStartTrial(sessionId: string | null): void {
  if (!realSessionId(sessionId)) return;
  // The plan from day one began no trial; nothing new is sent for it (Subscribe is the server's).
  if (checkoutOffered() === 'subscribe') return;
  void once(`saddle_start_trial_sent:${sessionId}`, () => trackPixel('StartTrial', { value: 0, currency: 'USD', trialDays: TRIAL_DAYS }));
}

/*
 * A trial Stripe has and the account does not show yet.
 *
 * Stripe sends the browser back the moment the card is taken; the webhook that grants the trial
 * can land later — later, sometimes, than the success page waits (BillingReturn's RESUME_WAIT_MS).
 * Until it does, /credits still says the free tier, so every screen that sells the trial sold it
 * AGAIN: review found the success page's "Back to your plan" led to "Start 7-day free trial", one
 * press opened a second checkout, and the server — which does not ask Stripe about an account with
 * no subscription recorded — made a second trial. Both would charge $20 on day 8. So the return is
 * remembered, and while the account has not caught up, the Plan screen's Build goes back to wait
 * for it and the Credits screen does not offer the trial.
 *
 * In sessionStorage, and for a day: nothing on the server has confirmed it — the id is whatever the
 * address said — so it lives no longer, and reaches no further, than the plan put away for the same
 * trip (draftStash.ts). Blocked storage remembers nothing, which is how this was before; the guard
 * that cannot be got round is the server's, and belongs to it (lane/trial-server).
 */
const AWAITED_KEY = 'saddle.trialCheckoutAwaited';
const AWAITED_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface AwaitedCheckout {
  ownerId: string;
  sessionId: string;
  at: number;
}

/**
 * Whether the account shows a plan: the paid or unlimited tier, or a store billing it. Not credits
 * alone — an old account builds on those (buildsIncluded) while its trial may still be on the way.
 */
function showsPlan(balance: { tier?: string; subscribed?: boolean }): boolean {
  return balance.tier === 'paid' || balance.tier === 'unlimited' || balance.subscribed === true;
}

function readAwaited(): AwaitedCheckout | null {
  try {
    const record = JSON.parse(sessionStorage.getItem(AWAITED_KEY) ?? 'null') as Partial<AwaitedCheckout> | null;
    if (!record || typeof record.ownerId !== 'string' || typeof record.sessionId !== 'string' || typeof record.at !== 'number') {
      return null;
    }
    return record as AwaitedCheckout;
  } catch {
    return null;
  }
}

/** Called on the return from a subscription's checkout: Stripe has this account's trial now. */
export function rememberTrialCheckout(ownerId: string, sessionId: string | null): void {
  if (!ownerId || !realSessionId(sessionId)) return;
  try {
    sessionStorage.setItem(AWAITED_KEY, JSON.stringify({ ownerId, sessionId, at: Date.now() } satisfies AwaitedCheckout));
  } catch {
    /* see above: nothing remembered is how it was */
  }
}

/** Forget it — once the build it was kept for has begun, or the account shows the plan. */
export function forgetTrialCheckout(): void {
  try {
    sessionStorage.removeItem(AWAITED_KEY);
  } catch {
    /* nothing to forget */
  }
}

/**
 * The checkout session this tab came back from whose trial `balance` does not show yet, or null.
 * A balance that DOES show the plan is what was being waited for, so the record is forgotten then.
 */
export function trialCheckoutAwaited(
  ownerId: string,
  balance: { tier?: string; subscribed?: boolean; creditsRemaining?: number },
): string | null {
  const record = readAwaited();
  if (!record || !ownerId || record.ownerId !== ownerId) return null;
  if (showsPlan(balance) || !(Date.now() - record.at <= AWAITED_MAX_AGE_MS)) {
    forgetTrialCheckout();
    return null;
  }
  return record.sessionId;
}

/**
 * StartBuilding (trial14-and-demo handoff §4, 2026-10-12): once per visitor, when a build first
 * starts — the deepest step that happens often enough for Meta to optimise for. A custom event,
 * through the same consent rules as every other (trackPixel), recorded as sent only when it was.
 */
export function reportStartBuilding(): boolean {
  return once('saddle_start_building_sent', () => trackPixel('StartBuilding'));
}

/**
 * MakeOneTapped (V6, 2026-10-14): once per visitor, on the demo screen's first tap (demo_cta_pressed)
 * — the event one campaign optimises for, since it happens on most ad visits that go anywhere. The
 * same consent rules as StartBuilding, and recorded as sent only when it was.
 */
export function reportMakeOneTapped(): boolean {
  return once('saddle_make_one_tapped_sent', () => trackPixel('MakeOneTapped'));
}
